import type { PushSubscriptionType, SubscribePushNotificationsDto } from '@/queries/user/user.schema';
import { getBrowser } from '@/libs/userSource/getBrowser';
import { getDevice } from '@/libs/userSource/getDevice';
import { QueriesKeys } from '@/queries/queriesKeys';
import { getQueryClient } from '@/queries/queryClient';
import { tokenManager } from '@/queries/token-manager';
import { userApi } from '@/queries/user/user.api';

export const ONE_SIGNAL_SCRIPT_SRC = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';

type OneSignalSDK = any;
export type PushSupportReason = 'api_unsupported' | 'ios_not_standalone' | null;
export type PushSupportState = {
  supported: boolean;
  reason: PushSupportReason;
};
export type EnableOneSignalPushResultReason = 'sdk_not_ready' | 'permission_denied' | 'unsupported' | 'unknown';
export type EnableOneSignalPushResult = {
  enabled: boolean;
  reason: EnableOneSignalPushResultReason | null;
};

declare global {
  interface Window {
    OneSignalDeferred?: Array<(OneSignal: OneSignalSDK) => void | Promise<void>>;
    cioanalytics?: {
      identify: (id: string, data: Record<string, unknown>) => void;
    };
  }
}

const WAIT_FOR_OPT_IN_MS = 12_000;
// Debounce delay for backend subscription sync triggered by PushSubscription 'change' events.
// login() migration after optIn() fires a second 'change' event within milliseconds;
// coalescing them into one call prevents duplicate token syncs to the backend.
const SYNC_BACKEND_DEBOUNCE_MS = 2_000;

let oneSignalReadyPromise: Promise<OneSignalSDK> | null = null;
let oneSignalInstance: OneSignalSDK | null = null;
let syncedExternalUserId: string | null = null;
let syncBackendDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const oneSignalReadySubscribers = new Set<() => void>();

function getPushSubscriptionType(): PushSubscriptionType {
  if (typeof window === 'undefined') {
    return 'chrome-push';
  }

  const ua = navigator.userAgent;
  const platform = navigator.platform || '';

  if (/Firefox/i.test(ua)) {
    return 'firefox-push';
  }

  // Safari (iOS/macOS) reports Safari in UA but not Chrome/Edge.
  if (/Safari/i.test(ua) && !/Chrome|Chromium|Edg/i.test(ua)) {
    return 'safari-push';
  }

  if (/Win/i.test(platform)) {
    return 'windows-push';
  }

  if (/Mac/i.test(platform)) {
    return 'mac-os-push';
  }

  return 'chrome-push';
}

async function syncPushNotificationSubscriptionWithBackend(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  // Only attempt when user is authenticated; /users/me requires a valid access token.
  const accessToken = tokenManager.getAccessToken();
  if (!accessToken) {
    return;
  }

  const token = oneSignalInstance?.User?.PushSubscription?.token as string | undefined;
  if (!token) {
    return;
  }

  const type: PushSubscriptionType = getPushSubscriptionType();
  const dto: SubscribePushNotificationsDto = { token, type };

  await userApi.subscribePushNotifications(dto).then(() => {
    getQueryClient().invalidateQueries({ queryKey: [QueriesKeys.mePlayerQuests] });
  }).catch(() => {
    // Backend is responsible for deduplication and handling invalid subscriptions.
    // Keep push UX resilient; failures are non-fatal and can be inspected via logs if needed.
  });
}

export function ensureOneSignalReady(appId: string): Promise<OneSignalSDK> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('OneSignal is only available in the browser'));
  }

  if (oneSignalReadyPromise) {
    return oneSignalReadyPromise;
  }

  oneSignalReadyPromise = new Promise<OneSignalSDK>((resolve) => {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal) => {
      await OneSignal.init({
        appId,
        safari_web_id: 'web.onesignal.auto.4f716432-69c5-4efb-9ef6-8b17a5658ee8',
        notifyButton: { enable: false },
        // Disable the built-in "Thank you for subscribing" push notification.
        // The app handles subscription confirmation UX via in-app toasts instead.
        welcomeNotification: { disable: true },
      });
      oneSignalInstance = OneSignal;

      OneSignal?.User?.PushSubscription?.addEventListener?.('change', () => {
        // Debounce to coalesce rapid consecutive 'change' events (e.g. optIn() followed
        // immediately by login() migration) into a single backend sync call.
        if (syncBackendDebounceTimer !== null) {
          clearTimeout(syncBackendDebounceTimer);
        }
        syncBackendDebounceTimer = setTimeout(() => {
          syncBackendDebounceTimer = null;
          void syncPushNotificationSubscriptionWithBackend();
        }, SYNC_BACKEND_DEBOUNCE_MS);
      });

      oneSignalReadySubscribers.forEach((callback) => {
        callback();
      });
      resolve(OneSignal);
    });
  });

  return oneSignalReadyPromise;
}

export function isOneSignalSdkReady(): boolean {
  return Boolean(oneSignalInstance);
}

export function subscribeToOneSignalReady(callback: () => void): () => void {
  oneSignalReadySubscribers.add(callback);
  return () => {
    oneSignalReadySubscribers.delete(callback);
  };
}

/**
 * Syncs OneSignal user identity with the app account.
 * When `appId` is passed, always awaits `ensureOneSignalReady` first so identity work never
 * no-ops because `oneSignalReadyPromise` was not created yet.
 */
export async function syncOneSignalIdentity(userId?: string, appId?: string): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  if (appId) {
    await ensureOneSignalReady(appId);
  }

  if (!oneSignalReadyPromise) {
    return;
  }

  const OneSignal = await oneSignalReadyPromise;

  if (userId && syncedExternalUserId !== userId) {
    await OneSignal.login?.(userId);
    syncedExternalUserId = userId;
    return;
  }

  if (!userId && syncedExternalUserId) {
    await OneSignal.logout?.();
    syncedExternalUserId = null;
  }
}

function isBrowserPushApiSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

function isIosLikeDevice(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const platform = navigator.platform;
  const userAgent = navigator.userAgent;

  return /iPad|iPhone|iPod/.test(platform)
    || /iPad|iPhone|iPod/.test(userAgent)
    || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.matchMedia('(display-mode: standalone)').matches
    || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

export function getPushSupportState(): PushSupportState {
  if (isIosLikeDevice() && !isStandaloneMode()) {
    return {
      supported: false,
      reason: 'ios_not_standalone',
    };
  }

  if (!isBrowserPushApiSupported()) {
    return {
      supported: false,
      reason: 'api_unsupported',
    };
  }

  return {
    supported: true,
    reason: null,
  };
}

export function isOneSignalPushSupported(): boolean {
  return getPushSupportState().supported;
}

export async function getOneSignalPushEnabled(appId: string, userId?: string): Promise<boolean> {
  const supportState = getPushSupportState();
  if (!supportState.supported) {
    return false;
  }

  const OneSignal = await ensureOneSignalReady(appId);
  await syncOneSignalIdentity(userId, appId);
  const optedIn = Boolean(OneSignal?.User?.PushSubscription?.optedIn);
  return optedIn;
}

/**
 * Wait for OneSignal to report optedIn after optIn(), with a timeout.
 * On iOS the subscription can be created asynchronously, so optedIn may stay false
 * until the SDK fires the PushSubscription "change" event.
 */
function waitForOptedIn(OneSignal: OneSignalSDK): Promise<boolean> {
  if (OneSignal?.User?.PushSubscription?.optedIn) {
    return Promise.resolve(true);
  }
  return new Promise<boolean>((resolve) => {
    const timeoutId = setTimeout(() => {
      // eslint-disable-next-line ts/no-use-before-define
      OneSignal?.User?.PushSubscription?.removeEventListener?.('change', listener);
      const final = Boolean(OneSignal?.User?.PushSubscription?.optedIn);
      resolve(final);
    }, WAIT_FOR_OPT_IN_MS);

    const listener = (event: { current?: { optedIn?: boolean } }) => {
      if (event?.current?.optedIn) {
        clearTimeout(timeoutId);
        OneSignal?.User?.PushSubscription?.removeEventListener?.('change', listener);
        resolve(true);
      }
    };

    OneSignal?.User?.PushSubscription?.addEventListener?.('change', listener);
  });
}

function identifyOneSignalSubscription(user?: { id?: string; email?: string }) {
  if (!user?.id) {
    return;
  }

  const subscriptionId = oneSignalInstance?.User?.PushSubscription?.id;
  if (!subscriptionId) {
    return;
  }

  window.cioanalytics?.identify(user.id, {
    email: user.email,
    subscription_status: true,
    onesignal_id: subscriptionId,
    device: getDevice(),
    browser: getBrowser(),
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    channel: 'web',
  });
}

/**
 * Must be invoked from a direct user gesture.
 * The initial optIn() call happens synchronously, before any awaited work.
 */
export async function enableOneSignalPushFromGesture(
  user?: { id?: string; email?: string },
): Promise<EnableOneSignalPushResult> {
  const supportState = getPushSupportState();
  if (!supportState.supported) {
    return { enabled: false, reason: 'unsupported' };
  }

  const OneSignal = oneSignalInstance;
  if (!OneSignal) {
    return { enabled: false, reason: 'sdk_not_ready' };
  }

  // Do not await anything before this line; Safari requires direct user-gesture context.
  const optInPromise = OneSignal?.User?.PushSubscription?.optIn?.();

  if (optInPromise && typeof optInPromise.then === 'function') {
    await optInPromise;
  }

  let optedIn = Boolean(OneSignal?.User?.PushSubscription?.optedIn);

  if (!optedIn) {
    optedIn = await waitForOptedIn(OneSignal);
  }

  if (!optedIn) {
    const reason: EnableOneSignalPushResultReason = Notification.permission === 'denied'
      ? 'permission_denied'
      : 'unknown';
    return { enabled: false, reason };
  }

  // login() is called after optIn() because calling it before puts OneSignal's User
  // context into a transitioning state, causing optIn() to operate on a stale
  // PushSubscription object and silently fail. The 'change' event debounce in
  // ensureOneSignalReady coalesces the two rapid change events (subscription creation
  // + login migration) into a single backend sync, preventing duplicate token calls.
  void syncOneSignalIdentity(user?.id);
  identifyOneSignalSubscription(user);

  return { enabled: true, reason: null };
}

export async function setOneSignalPushEnabled(
  appId: string,
  enabled: boolean,
  user?: { id?: string; email?: string },
): Promise<boolean> {
  if (enabled) {
    const result = await enableOneSignalPushFromGesture(user);
    return result.enabled;
  }

  const supportState = getPushSupportState();
  if (!supportState.supported) {
    return false;
  }

  const OneSignal = oneSignalInstance ?? await ensureOneSignalReady(appId);
  await syncOneSignalIdentity(user?.id, appId);
  try {
    await OneSignal?.User?.PushSubscription?.optOut?.();
  } catch (error) {
    // OneSignal can reject opt-out with subscription-10 if payload is internally inconsistent.
    // Treat this as non-fatal and keep local UX consistent with "disabled" intent.
    const oneSignalErrors = (error as { errors?: Array<{ code?: string }> } | undefined)?.errors;
    const isSubscriptionDisabledNotificationTypesConflict = oneSignalErrors?.some(item => item?.code === 'subscription-10');

    if (!isSubscriptionDisabledNotificationTypesConflict) {
      throw error;
    }
  }
  return false;
}
